import React, { Fragment } from "react";
import PageHead from "../../components/pageHead";
import useMediaQuery from "../../hooks/useMediaQuery";
import MobileNav from "../../components/mobileNav";
import TutorialHelpImage from "../../assets/images/tutorialHelp.png";
import TutorialConsumablesImage from "../../assets/images/tutorialConsumables.png";
import TutorialSnapshotImage from "../../assets/images/tutorialSnapshot.png";
import TutorialServiceImage from "../../assets/images/tutorialService.png";
import TutorialTrackDaysImage from "../../assets/images/tutorialTrackDays.png";
import TutorialHistoryImage from "../../assets/images/tutorialHistory.png";
import TutorialReportsImage from "../../assets/images/tutorialReports.png";
import TutorialChecklistImage from "../../assets/images/tutorialChecklist.png";
import QuickStartGuide from "../../assets/files/DBQuickStartGuide.pdf";
import styles from "./style.module.css";

const Tutorials = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <Fragment>
      <PageHead title="DashBook Tutorial" logo={isMobile} />
      <div className={styles.body}>
        <div className={styles.body_view}>
          <div className={styles.dashbook_tutorial}>
            <h2>Where to find Help</h2>
            <p>
              You can always find help by clicking the Help icon at the top
              right of each page. This will open page-specific instructions to
              help guide you.
            </p>
            <div className={styles.tutorial_image}>
              <img src={TutorialHelpImage} alt={"Where to find Help"} />
            </div>
            <h5>
              Tip: If you’re just getting started, download our{" "}
              <a
                href={QuickStartGuide}
                target="_blank"
                rel="noreferrer"
                title="DashBook Quick-Start Guide.pdf"
              >
                Quick-Start Guide
              </a>
              .
            </h5>
            <hr />
            <h2>Setting Up Your Consumables</h2>
            <p>
              If you take your car to the track, your consumables—such as tires,
              brakes, and fluids—are the main wear items you'll want to monitor
              closely. During your initial setup of DashBook, go to the{" "}
              <strong>Set Up Consumables</strong> page and follow the following
              steps:
            </p>
            <div className={styles.tutorial_image}>
              <img
                src={TutorialConsumablesImage}
                alt={"Setting Up Your Consumables"}
              />
            </div>
            <ol>
              <li>
                Choose the unit of measurement:{" "}
                <strong>track days, sessions, or miles</strong>. Most users
                prefer <strong>Track Days</strong> for simplicity. However, if
                you run slicks and prefer to monitor heat cycles, selecting{" "}
                <strong>Sessions</strong> may be more appropriate. Keep in mind
                that choosing <strong>Sessions</strong> means all other
                consumables will also be tracked by the number of sessions.
              </li>
              <li>
                Below, you will see a preset list of the most common Consumable
                items to monitor. You can{" "}
                <strong>
                  Deselect any items that <u>you do not wish</u> to track.
                </strong>{" "}
                You can{" "}
                <strong>
                  Add any additional custom items that you would like to monitor
                </strong>{" "}
                to the list by entering their description in the provided field
                above.
              </li>
              <li>
                <p>
                  Set the <strong>Expected Lifespan</strong> of each item by
                  clicking the Edit icon to the right of each line. representing
                  the number of days (or sessions/laps) you expect it to last
                  before requiring replacement. This will vary by item. For
                  example, tires may last <strong>12 track days</strong>, while
                  oil changes might be needed every{" "}
                  <strong>4-5 track days</strong>. If you're unsure, consult
                  your mechanic.
                </p>
                <p>
                  <strong>Tip:</strong> When first setting expected lifespans,{" "}
                  <strong>be conservative</strong>—round down to encourage
                  earlier inspections. It's always better to check too soon than
                  too late! Additionally, if you're using a new model or brand
                  of consumables, estimate their lifespan conservatively. For
                  example, a new brand or model of tires may not last as long as
                  your previous set.
                </p>
                <p>
                  <strong>Tip:</strong> Expect wear to increase as your pace
                  improves: e.g., a beginner’s tires might last a season, but as
                  you build speed, they may only last a few weekends. beginner’s
                  tires might last a season, but as you build speed, they may
                  only last a few weekends.
                </p>
              </li>
            </ol>
            <p>
              Note: Once you've set up your main consumables, you won’t need to
              revisit this page unless you want to update lifespans or add new
              items. Moving forward, you’ll update consumable statuses from the
              <strong>Add New Service</strong> page.
            </p>
            <hr />
            <h2>Snapshot Page</h2>
            <p>
              The <strong>Snapshot Page</strong> is your main dashboard in
              DashBook. Once you've set up consumables and logged track days and
              services, you'll find a{" "}
              <strong>summary of everything here</strong>. If you haven’t
              already done so, download our{" "}
              <a
                href={QuickStartGuide}
                target="_blank"
                rel="noreferrer"
                title="DashBook Quick-Start Guide.pdf"
              >
                Quick-Start Guide
              </a>{" "}
              for a step-by-step guide to setting up your DashBook account.
            </p>
            <p>
              If you’re missing some data initially, don’t worry—the page will
              become more accurate and informative as you continue to enter more
              information.{" "}
            </p>
            <div className={styles.tutorial_image}>
              <img src={TutorialSnapshotImage} alt={"Snapshot Page"} />
            </div>
            <p>
              Pay close attention to the <strong>Track Days Left</strong> field
              on the right—it shows how much life remains in each item. DashBook
              will alert you as service intervals approach:
            </p>
            <ol>
              <li>
                <strong>Yellow:</strong> 3 days remaining
              </li>
              <li>
                <strong>Red:</strong> 1 day remaining
              </li>
              <li>
                You can adjust these alert settings on your{" "}
                <strong>Profile Page</strong> (upper right corner of the
                screen).
              </li>
            </ol>
            <p>From the top of the Snapshot Page, you can quickly:</p>
            <ol>
              <li>
                <strong>Add New Service Entries</strong>
              </li>
              <li>
                <strong>Add New Track Days</strong>
              </li>
            </ol>
            <p>
              Additionally, you can{" "}
              <strong>download a printable version</strong> of your Snapshot to
              provide to your mechanic before tech inspections or service
              appointments.
            </p>
            <hr />
            <h2>Entering New Service</h2>
            <p>
              On this page, log the <strong>maintenance and repairs</strong>{" "}
              performed on your vehicle. This includes both consumable items and
              long-term services such as repairs and upgrades. Only consumable
              items will appear on your <strong>Snapshot Page</strong>, while
              all other service records will be stored in{" "}
              <strong>Service History</strong>.
            </p>
            <div className={styles.tutorial_image}>
              <img src={TutorialServiceImage} alt={"Entering New Service"} />
            </div>
            <p>When entering a service item:</p>
            <ol>
              <li>
                Select the <strong>Consumables category</strong> if applicable
                (e.g., Oil Change, Brake pads, etc.). If you want to add a
                custom category, you can add it from the Utilities menu {" > "}
                Set Up Consumables page.
              </li>
              <li>
                For non-consumables (e.g., A/C repair), select{" "}
                <strong>N/A - Not Consumable</strong>.
              </li>
              <li>
                Enter the{" "}
                <strong> date, description, provider, and cost</strong>.
              </li>
              <li>
                Use the <strong>comments field</strong> for part numbers,
                installation notes, or supplier contact info.
              </li>
              <li>Upload files such as receipts or photos for reference.</li>
            </ol>
            <p>
              <strong>Tip:</strong> For the initial setup, enter only the{" "}
              <strong>most recent</strong> consumables service. You can add more
              records later to build a complete maintenance history – but keep
              in mind, DashBook can’t provide an accurate status on any item
              until the last service is logged.
            </p>
            <hr />
            <h2>Entering Track Days</h2>
            <p>
              Use this section to log your time on the track. Enter details such
              as the <strong>event name, date, and duration</strong>. Based on
              your consumable tracking settings (Days/Sessions/Miles), you’ll be
              prompted to enter corresponding data.
            </p>
            <p>For example:</p>
            <ul>
              <li>
                <em>Sebring PCA Event, Jan 1, 2025, 2 track days.</em>
              </li>
            </ul>
            <div className={styles.tutorial_image}>
              <img src={TutorialTrackDaysImage} alt={"Entering Track Days"} />
            </div>
            <p>
              <strong>
                Enter as many previous events as you need to provide the usage
                of all your Consumable items
              </strong>
              . For long-life parts like brake rotors, that may require quite a
              few track days to get a complete usage history. If you don’t
              recall all the track days, that’s OK, you can enter more later.
              Just remember - DashBook won’t be able to provide an accurate
              status of any items that don’t include all the track events in
              their history.
            </p>
            <p>
              <strong>Tip #1</strong>: The # of Days field accepts decimals, so
              if you only complete half the sessions on <strong>Day 2</strong>,
              you can enter <strong>1.5 days</strong>, and DashBook will adjust
              wear calculations accordingly.
            </p>
            <p>
              <strong>Tip #2</strong>: You can also add track days at the top of
              the <strong>Track History Page</strong>.
            </p>
            <hr />
            <h2>Service History Page</h2>
            <p>
              This section stores all past maintenance records. At the top, use
              the <strong>search bar</strong> to find specific entries, or
              scroll through and sort by column.
            </p>
            <ul>
              <li>
                To <strong>edit</strong> an entry, click the{" "}
                <strong>Edit icon</strong>.
              </li>
              <li>
                To <strong>delete</strong> an entry, click the{" "}
                <strong>Trash icon</strong>.
              </li>
              <li>
                To <strong>add a new service entry</strong>, click{" "}
                <strong>Add New Service</strong> at the top.
              </li>
            </ul>
            <div className={styles.tutorial_image}>
              <img src={TutorialHistoryImage} alt={"Service History Page"} />
            </div>
            <hr />
            <h2>Reports Page</h2>
            <p>
              The <strong>Reports Page</strong> provides an overview of vehicle
              expenses. You can select the date range from Year to Date, Last 12
              Months, or set a custom date range.
            </p>
            <p>
              Then, filter your report by <strong>Consumables Only</strong> or
              <strong>All Maintenance Expenses</strong>.
            </p>
            <p>
              Your total expenses will be displayed at the bottom of the page
              and you can download a printable report via the link at the bottom
              of the page.
            </p>
            <div className={styles.tutorial_image}>
              <img src={TutorialReportsImage} alt={"Reports Page"} />
            </div>
            <hr />
            <h2>Checklist Page</h2>
            <p>
              The <strong>Checklist Page</strong> helps you pack essential items
              before a track day. This list can include:
            </p>
            <ul>
              <li>
                <strong>Car-related gear:</strong> e.g., torque wrench, tire
                pressure gauge, tools, jack
              </li>
              <li>
                <strong>Personal items:</strong> e.g., helmet, gloves,
                sunglasses, tech form, chair, cooler, snacks, etc.
              </li>
            </ul>
            <p>
              Once your checklist is created, simply check off each item as you
              pack. You can:
            </p>
            <ul>
              <li>
                <strong>Sort</strong> items by checked/unchecked status
              </li>
              <li>
                <strong>Reset</strong> the checklist before each new track event
              </li>
            </ul>
            <p>
              This ensures you never forget an important item before hitting the
              track!
            </p>
            <div className={styles.tutorial_image}>
              <img src={TutorialChecklistImage} alt={"Checklist Page"} />
            </div>
          </div>
        </div>
      </div>
      {isMobile && <MobileNav defaultActive={true} isScrolling={false} />}
    </Fragment>
  );
};

export default Tutorials;
